// ABOUTME: Hosts an EditNode form field outside the EditNode form, for the grid's inline editors.
// ABOUTME: Owns a one-field react-hook-form, the save/cancel actions and the commit into a node content.

import { Button, Stack } from '@ds/coolshop';
import type { NodeContentValueO } from '@ds/data-table';
import type { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

/** The single form path every hosted field is bound to. */
export const STANDALONE_FIELD_NAME = 'value';

/**
 * The subset of `BaseProps` an EditNode field needs to render on its own. Fields read far more
 * props than they require, so the unused ones are filled with inert values here.
 */
export type StandaloneFieldProps = {
    id: string;
    labelId: string;
    name: string;
    control: any;
    label: string;
    isMultiValue: boolean;
    isMultiLanguage: boolean;
    measureFieldContext: string;
    disabled: boolean;
    readOnly: boolean;
    InputProps: { endAdornment: React.ReactElement<any, any> };
    getToggleTranslationName: () => string;
};

type Props = {
    /** Current value of the field, in the shape the hosted field expects. */
    initialValue: unknown;
    label: string;
    isMultiValue?: boolean;
    /** Disables the save action while the previous commit is still in flight. */
    saving?: boolean;
    onCancel: () => void;
    onSave: (value: unknown) => void;
    children: (fieldProps: StandaloneFieldProps) => ReactNode;
};

/**
 * Writes `value` into the entry of `languageIsoCode`.
 *
 * A content that has no entry for that language is single-language content stored under whichever
 * language it was created with, so its only entry is the one to write rather than a new one.
 */
export function writeLanguageEntry(
    entries: NodeContentValueO[],
    languageIsoCode: string,
    value: unknown[],
): NodeContentValueO[] {
    if (!entries.length) {
        return [{ languageIsoCode, value }];
    }

    const target = entries.some(entry => entry.languageIsoCode === languageIsoCode)
        ? languageIsoCode
        : entries[0].languageIsoCode;

    return entries.map(entry => (entry.languageIsoCode === target ? { ...entry, value } : entry));
}

export function StandaloneField(props: Props) {
    const { initialValue, label, isMultiValue = false, saving, onCancel, onSave, children } = props;
    const intl = useIntl();

    const methods = useForm({ defaultValues: { [STANDALONE_FIELD_NAME]: initialValue } });

    const fieldProps: StandaloneFieldProps = {
        id: STANDALONE_FIELD_NAME,
        labelId: STANDALONE_FIELD_NAME,
        name: STANDALONE_FIELD_NAME,
        control: methods.control,
        label,
        isMultiValue,
        isMultiLanguage: false,
        measureFieldContext: '',
        disabled: false,
        readOnly: false,
        InputProps: { endAdornment: <span /> },
        getToggleTranslationName: () => '',
    };

    return (
        <FormProvider {...methods}>
            <Stack gap={1} padding={2} minWidth="24rem">
                {children(fieldProps)}
                <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button variant="text" color="primary" onClick={onCancel}>
                        {intl.formatMessage({
                            id: 'search-detail.edit-cell.cancel',
                            defaultMessage: 'Cancel',
                        })}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        onClick={() => onSave(methods.getValues(STANDALONE_FIELD_NAME))}
                    >
                        {intl.formatMessage({
                            id: 'search-detail.edit-cell.save',
                            defaultMessage: 'Save',
                        })}
                    </Button>
                </Stack>
            </Stack>
        </FormProvider>
    );
}
